import { deleteVeiculo, getVeiculos } from '@spark/services/frotas';
import { useState, useEffect, useCallback, useRef } from 'react';
import { notification } from '@spark/ui-components/utils/notification';

const normalizarSituacao = (valor) => {
  if (valor === undefined || valor === null) return 0;
  const normalizado = String(valor).trim().toUpperCase();
  return normalizado === 'ATIVO' || normalizado === '1' || normalizado === 'TRUE' ? 1 : 0;
};

export const useVeiculosListagem = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(5);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [veiculoToDelete, setVeiculoToDelete] = useState(null);
  const isMountedRef = useRef(true);

  const [filtros, setFiltros] = useState({
    frota: '',
    placa: '',
    renavam: '',
    uf: '',
  });

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const fetchData = useCallback(
    async (filtrosParaBuscar = filtros) => {
      if (!isMountedRef.current) return;

      try {
        setLoading(true);

        const renavamFiltro =
          filtrosParaBuscar.renavam && typeof filtrosParaBuscar.renavam === 'string'
            ? filtrosParaBuscar.renavam.replace(/\D+/g, '')
            : filtrosParaBuscar.renavam || '';
        const ufFiltro =
          filtrosParaBuscar.uf && typeof filtrosParaBuscar.uf === 'string'
            ? filtrosParaBuscar.uf.trim().toUpperCase()
            : filtrosParaBuscar.uf || '';

        const filters = {
          quantidadePorPagina,
          pagina: paginaAtual,
          ...(filtrosParaBuscar.frota ? { frota: filtrosParaBuscar.frota } : {}),
          ...(filtrosParaBuscar.placa ? { placa: filtrosParaBuscar.placa } : {}),
          ...(renavamFiltro ? { renavam: renavamFiltro } : {}),
          ...(ufFiltro ? { uf: ufFiltro } : {}),
        };

        const responseData = await getVeiculos(filters);
        if (!isMountedRef.current) return;

        const registrosResponse = responseData?.registros ?? responseData?.Registros ?? [];
        const registros = Array.isArray(registrosResponse) ? registrosResponse : [];

        const dadosProcessados = registros.map((item) => {
          const modelo = item?.modeloVeicular ?? item?.ModeloVeicular ?? {};

          return {
            id: item.id ?? item.Id ?? item.idVeiculo ?? item.IdVeiculo,
            frota: item.frota ?? item.Frota ?? '-',
            placa: item.placa ?? item.Placa ?? '-',
            renavam: (() => {
              const valor = item.renavam ?? item.Renavam;
              if (valor === undefined || valor === null || valor === '') return '-';
              return String(valor);
            })(),
            uf: (() => {
              const ufValor = item.uf ?? item.Uf ?? item.UF;
              if (ufValor === undefined || ufValor === null) return '-';
              return String(ufValor).toUpperCase();
            })(),
            descricao: modelo.descricao ?? modelo.Descricao ?? '-',
            nomeTipoModeloVeicular: modelo.nomeTipoModeloVeicular ?? modelo.NomeTipoModeloVeicular ?? '-',
            codigoTipoModeloVeicular: modelo.codigoTipoModeloVeicular ?? modelo.CodigoTipoModeloVeicular ?? '-',
            unidadeCapacidade: modelo.unidadeCapacidade ?? modelo.UnidadeCapacidade ?? '-',
            capacidadeModelo: modelo.capacidadeModelo ?? modelo.CapacidadeModelo ?? null,
            numeroEixos: modelo.numeroEixos ?? modelo.NumeroEixos ?? null,
            ocupacaoCubicaPallets: modelo.ocupacaoCubicaPallets ?? modelo.OcupacaoCubicaPallets ?? null,
            toleranciaMinimaCarregamento:
              modelo.toleranciaMinimaCarregamento ?? modelo.ToleranciaMinimaCarregamento ?? null,
            codigoIntegracao: modelo.codigoIntegracao ?? modelo.CodigoIntegracao ?? '-',
            situacao: normalizarSituacao(modelo.situacao ?? modelo.Situacao ?? modelo.status ?? modelo.Status),
          };
        });

        setData(dadosProcessados);

        const total =
          typeof responseData?.totalRegistros === 'number'
            ? responseData.totalRegistros
            : typeof responseData?.TotalRegistros === 'number'
              ? responseData.TotalRegistros
              : registros.length;
        setTotalRegistros(total);

        const paginaApi =
          typeof responseData?.paginaAtual === 'number'
            ? responseData.paginaAtual
            : typeof responseData?.PaginaAtual === 'number'
              ? responseData.PaginaAtual
              : undefined;
        if (typeof paginaApi === 'number' && paginaApi > 0 && paginaApi !== paginaAtual) {
          setPaginaAtual(paginaApi);
        }

        const quantidadeApi =
          typeof responseData?.quantidadePorPagina === 'number'
            ? responseData.quantidadePorPagina
            : typeof responseData?.QuantidadePorPagina === 'number'
              ? responseData.QuantidadePorPagina
              : undefined;
        if (
          typeof quantidadeApi === 'number' &&
          quantidadeApi > 0 &&
          quantidadeApi !== quantidadePorPagina
        ) {
          setQuantidadePorPagina(quantidadeApi);
        }
      } catch (error) {
        console.error('Erro ao buscar veiculos:', error);
        notification.error('Erro ao carregar veiculos.');
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [filtros, paginaAtual, quantidadePorPagina],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterApply = useCallback(
    (newFilters) => {
      setFiltros(newFilters);
      setPaginaAtual(1);
      setFilterLoading(true);
      fetchData(newFilters).finally(() => {
        if (isMountedRef.current) {
          setFilterLoading(false);
        }
      });
    },
    [fetchData],
  );

  const handleLimparFiltros = useCallback(
    (clearedFilters) => {
      setFiltros(clearedFilters);
      setPaginaAtual(1);
      setFilterLoading(true);
      fetchData(clearedFilters).finally(() => {
        if (isMountedRef.current) {
          setFilterLoading(false);
        }
      });
    },
    [fetchData],
  );

  const handleExcluir = useCallback(
    (id) => {
      const veiculo = data.find((item) => item.id === id);
      setVeiculoToDelete({
        id,
        frota: veiculo?.frota || '-',
        placa: veiculo?.placa || '-',
      });
      setShowDeleteModal(true);
    },
    [data],
  );

  const confirmDelete = useCallback(async () => {
    if (!veiculoToDelete) return;

    try {
      await deleteVeiculo(veiculoToDelete.id);
      notification.success('Veiculo excluido com sucesso!');
      setShowDeleteModal(false);
      setVeiculoToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir veiculo:', error);
      notification.error('Nao foi possivel excluir o veiculo.');
    }
  }, [veiculoToDelete, fetchData]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setVeiculoToDelete(null);
  }, []);

  return {
    data,
    loading,
    filterLoading,
    totalRegistros,
    paginaAtual,
    quantidadePorPagina,
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    confirmDelete,
    cancelDelete,
    showDeleteModal,
    veiculoToDelete,
    setPaginaAtual,
    setQuantidadePorPagina,
  };
};
